import React, { useState, useEffect, useMemo, useRef } from "react";
import UserService from "../services/UserService";
import { useAuth } from "react-oidc-context";
import { TextField, Autocomplete } from "@mui/material";
import { debounce } from "lodash";

interface UserOption {
  label: string;
  value: string;
}

interface UserNamePickerProps {

  handleOrderingForNameChange: (label: string, value?: string) => void;
  setValidationError: React.Dispatch<React.SetStateAction<string>>;
  selectedUserName?: string;
  className?: string;
}

const UserNamePicker: React.FC<UserNamePickerProps> = ({
  handleOrderingForNameChange,
  setValidationError,
  selectedUserName = '',
  className
}) => {
  const [userNameOptions, setUserNameOptions] = useState<UserOption[]>([]);
  const [userName, setUserName] = useState<UserOption | null>(
    selectedUserName ? { label: selectedUserName, value: selectedUserName } : null
  );
  const [inputValue, setInputValue] = useState("");

  const auth = useAuth();
  const controller = useMemo(() => new AbortController(), []);
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadUserNameOptions = useMemo(() => {
    const fetchOptions = async (input: string) => {
      if (!input || input.length < 3) return;

      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await UserService.getUsers(
          auth.user?.access_token as string,
          input,
          "userName",
          "asc",
          1,
          100,
          controller
        );
        const options = response?.rows?.map((user: { firstName: string; lastName: string, email: string }) => ({
          label: `${user.firstName} ${user.lastName} (${user.email})`,
          value: `${user.email}`,
        }));
        setUserNameOptions(options);
      } catch (error: any) {
        if (error.name === "CanceledError") {
          // Request was canceled
          return;
        }
        console.error("Failed to fetch users:", error);
      }
    };

    return debounce(fetchOptions, 300);
  }, [auth.user?.access_token]);


  useEffect(() => {
    return () => {
      loadUserNameOptions.cancel();
      controller.abort();
    };
  }, [loadUserNameOptions, controller]);

  return (
    <Autocomplete
     className={className}
      freeSolo
      options={userNameOptions}
      value={userName}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);

        if (newInputValue.trim() !== "") {
          const formattedUser = { label: newInputValue, value: newInputValue };
          setUserName(formattedUser);
          handleOrderingForNameChange(newInputValue);
          setValidationError("");
          loadUserNameOptions(newInputValue);
        } else {
          setValidationError("Invalid username.");
        }
      }}
      onChange={(_, selectedOption) => {

        if (typeof selectedOption === "string") {
          const newOption = { label: selectedOption, value: selectedOption };
          setUserName(newOption);
          handleOrderingForNameChange(selectedOption);
          setValidationError("");
        } else if (selectedOption) {
          setUserName(selectedOption);
          handleOrderingForNameChange(selectedOption.label, selectedOption.value);
          setValidationError("");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label=""
          error={!!inputValue && inputValue.trim() === ""}
          helperText={inputValue && inputValue.trim() === "" ? "Invalid username." : ""}
          InputProps={{
            ...params.InputProps,
            style: {
              height: "39px",
              fontSize: "14px",
              padding: "5px",
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "black", borderWidth: "0.1px" }, // Thin dark gray border
              "& input": { fontWeight: "normal" } // Ensures normal font weight
            }
          }}
        />
      )}
    />
  );
};

export default UserNamePicker;
